/**
 * Admin — Broadcast detail · Consent topics + detail · Cert verifier.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, Modal, Popconfirm, Segmented, Select, Skeleton,
  Space, Statistic, Switch, Table, Tag, Typography, message,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, SafetyCertificateOutlined, SearchOutlined } from '@ant-design/icons';
import type {
  BroadcastRecipientDetailRow, ConsentDetailStudentRow, ConsentTopicRow,
  CreateConsentTopicRequest, IssuedCertVerification,
} from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

function extractErrMsg(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) return (err.body as { message?: string } | null)?.message ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

/* ================== Broadcast detail ================== */

const RECIPIENT_TONE = { queued: 'default', delivered: 'success', failed: 'error', opened: 'processing' } as const;

export function BroadcastDetailPage() {
  const { institution = '', code = '' } = useParams<{ institution: string; code: string }>();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['broadcast-detail', institution, code],
    queryFn: () => productApi.getBroadcastDetail(institution, code),
    enabled: !!institution && !!code,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const b = q.data.broadcast;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button icon={<ArrowLeftOutlined />} type="text" size="small"
          onClick={() => navigate(`/i/${institution}/admin/comms/broadcasts`)}>Back</Button>
      </div>
      <Card size="small">
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
          <Typography.Title level={3} style={{ margin: 0 }}>{b.subject}</Typography.Title>
          <Tag>{b.channel}</Tag>
          <Tag color={b.status === 'sent' ? 'success' : b.status === 'failed' ? 'error' : 'default'}>{b.status}</Tag>
          <div style={{ flex: 1 }} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {b.sent_at ? `sent ${new Date(b.sent_at).toLocaleString()}` : `created ${new Date(b.created_at).toLocaleString()}`}
            {b.sent_by && ` · by ${b.sent_by}`}
          </Typography.Text>
        </div>
        <div style={{ marginTop: 10, fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{b.body}</div>
        <div style={{ marginTop: 10, fontSize: 12, color: '#8a94a6' }}>
          Audience filter: <code>{b.audience_filter}</code>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
        <Card size="small"><Statistic title="Recipients" value={b.recipient_count} /></Card>
        <Card size="small"><Statistic title="Queued" value={q.data.counts.queued} /></Card>
        <Card size="small"><Statistic title="Delivered" value={q.data.counts.delivered} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Opened" value={q.data.counts.opened} valueStyle={{ color: '#2f6ab0' }} /></Card>
        <Card size="small"><Statistic title="Failed" value={q.data.counts.failed} valueStyle={{ color: q.data.counts.failed > 0 ? '#b3382f' : '#14213d' }} /></Card>
      </div>

      <Card size="small" title={`Recipients (${q.data.recipients.length})`}>
        {q.data.recipients.length === 0
          ? <Empty description="No recipients (audience filter matched nobody)." style={{ padding: 20 }} />
          : (
            <Table<BroadcastRecipientDetailRow>
              size="small" pagination={{ pageSize: 30 }} rowKey="id"
              dataSource={q.data.recipients}
              columns={[
                { title: 'Guardian', key: 'g',
                  render: (_, r) => <>
                    {r.guardian_name ? <b>{r.guardian_name}</b> : <Typography.Text type="secondary">(unknown)</Typography.Text>}
                    <div style={{ fontSize: 11, color: '#8a94a6', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.guardian_phone}</div>
                  </> },
                { title: 'Wards', key: 'w',
                  render: (_, r) => r.student_names.length > 0
                    ? <Space size={4} wrap>{r.student_names.map((n, i) => <Tag key={i}>{n}</Tag>)}</Space>
                    : <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Status', dataIndex: 'status', key: 's', width: 110,
                  render: (v: keyof typeof RECIPIENT_TONE) => <Tag color={RECIPIENT_TONE[v]}>{v}</Tag> },
                { title: 'Delivered', dataIndex: 'delivered_at', key: 'd', width: 160,
                  render: (v: string | null) => v ? new Date(v).toLocaleString() : <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Note', dataIndex: 'note', key: 'n',
                  render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
              ]}
            />
          )}
      </Card>
    </Space>
  );
}

/* ================== Consent topics ================== */

export function ConsentsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['consents', institution],
    queryFn: () => productApi.listConsentTopics(institution),
    enabled: !!institution,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Consents</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            C-07 · one row per topic. Click a row to see who's signed and who hasn't.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>New topic</Button>
      </div>
      <Card size="small">
        {q.data.rows.length === 0
          ? <Empty description="No consent topics yet." style={{ padding: 20 }} />
          : (
            <Table<ConsentTopicRow>
              size="small" pagination={false} rowKey="id"
              dataSource={q.data.rows}
              onRow={(r) => ({ onClick: () => navigate(`/i/${institution}/admin/comms/consents/${r.code}`), style: { cursor: 'pointer' } })}
              columns={[
                { title: 'Code', dataIndex: 'code', key: 'c', width: 140,
                  render: (v: string) => <Tag style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</Tag> },
                { title: 'Title', dataIndex: 'title', key: 't' },
                { title: 'Yes', dataIndex: 'yes', key: 'y', width: 80, align: 'right',
                  render: (v: number) => <b style={{ color: '#1c6442' }}>{v}</b> },
                { title: 'No', dataIndex: 'no', key: 'n', width: 80, align: 'right',
                  render: (v: number) => <b style={{ color: '#a24040' }}>{v}</b> },
                { title: 'Pending', key: 'p', width: 100, align: 'right',
                  render: (_, r) => <span>{Math.max(0, r.eligible - r.responses)}</span> },
                { title: 'Active', dataIndex: 'active', key: 'a', width: 90,
                  render: (v: boolean) => v ? <Tag color="success">active</Tag> : <Tag>closed</Tag> },
              ]}
            />
          )}
      </Card>
      <ConsentAddModal open={addOpen} institution={institution}
        onClose={() => setAddOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['consents', institution] })} />
    </Space>
  );
}

function ConsentAddModal({
  open, institution, onClose, onDone,
}: { open: boolean; institution: string; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<CreateConsentTopicRequest>();
  const m = useMutation({
    mutationFn: (input: CreateConsentTopicRequest) => productApi.createConsentTopic(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Created'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="New consent topic" onCancel={() => { m.reset(); onClose(); }}
      okText="Create" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        onFinish={(v) => m.mutate({
          code: v.code.trim().toUpperCase(),
          title: v.title.trim(),
          description: v.description.trim(),
        })}>
        <Form.Item name="code" label="Code" rules={[{ required: true, max: 30 }]}>
          <Input placeholder="e.g. SPORTS-DAY-2026" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
        </Form.Item>
        <Form.Item name="title" label="Title" rules={[{ required: true, max: 200 }]}>
          <Input placeholder="e.g. Sports day away trip permission" />
        </Form.Item>
        <Form.Item name="description" label="Description shown to guardian" rules={[{ required: true, min: 1, max: 2000 }]}>
          <Input.TextArea rows={5} />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

export function ConsentDetailPage() {
  const { institution = '', code = '' } = useParams<{ institution: string; code: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'yes' | 'no'>('all');
  const q = useQuery({
    queryKey: ['consent-detail', institution, code],
    queryFn: () => productApi.getConsentDetail(institution, code),
    enabled: !!institution && !!code,
  });
  const toggle = useMutation({
    mutationFn: (active: boolean) => productApi.updateConsentTopic(institution, code, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consent-detail', institution, code] }),
  });
  const submit = useMutation({
    mutationFn: ({ student_id, choice }: { student_id: string; choice: 'yes' | 'no' }) =>
      productApi.submitConsent(institution, code, {
        topic_id: q.data!.topic.id, student_id, choice,
      }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['consent-detail', institution, code] }); void message.success('Recorded'); },
    onError: (e) => void message.error(extractErrMsg(e) ?? 'Failed'),
  });

  const filtered = useMemo(() => {
    const rows = q.data?.rows ?? [];
    if (statusFilter === 'all') return rows;
    if (statusFilter === 'pending') return rows.filter((r) => r.choice === null);
    return rows.filter((r) => r.choice === statusFilter);
  }, [q.data, statusFilter]);

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const t = q.data.topic;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button icon={<ArrowLeftOutlined />} type="text" size="small"
          onClick={() => navigate(`/i/${institution}/admin/comms/consents`)}>Back</Button>
      </div>
      <Card size="small">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <Tag style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{t.code}</Tag>
          <Typography.Title level={3} style={{ margin: 0 }}>{t.title}</Typography.Title>
          <div style={{ flex: 1 }} />
          <Space size={6} align="center">
            <span style={{ fontSize: 12, color: '#8a94a6' }}>active</span>
            <Switch checked={t.active} onChange={(v) => toggle.mutate(v)} />
          </Space>
        </div>
        <div style={{ marginTop: 8, fontSize: 13, color: '#3f4a63', whiteSpace: 'pre-wrap' }}>{t.description}</div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        <Card size="small"><Statistic title="Total students" value={q.data.counts.total} /></Card>
        <Card size="small"><Statistic title="Yes" value={q.data.counts.yes} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="No" value={q.data.counts.no} valueStyle={{ color: '#a24040' }} /></Card>
        <Card size="small"><Statistic title="Pending" value={q.data.counts.pending}
          valueStyle={{ color: q.data.counts.pending > 0 ? '#b3382f' : '#14213d' }} /></Card>
      </div>

      <Segmented value={statusFilter} onChange={(v) => setStatusFilter(v as typeof statusFilter)}
        options={[
          { label: 'All', value: 'all' },
          { label: `Pending (${q.data.counts.pending})`, value: 'pending' },
          { label: `Yes (${q.data.counts.yes})`, value: 'yes' },
          { label: `No (${q.data.counts.no})`, value: 'no' },
        ]} />

      <Card size="small">
        {filtered.length === 0
          ? <Empty description="No students in this filter." style={{ padding: 20 }} />
          : (
            <Table<ConsentDetailStudentRow>
              size="small" pagination={{ pageSize: 25 }} rowKey="student_id"
              dataSource={filtered}
              columns={[
                { title: 'Student', key: 's',
                  render: (_, r) => <>
                    <b>{r.student_name}</b>
                    <div style={{ fontSize: 11, color: '#8a94a6', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                      {r.admission_number} · {r.grade}{r.section ? `-${r.section}` : ''}
                    </div>
                  </> },
                { title: 'Guardian', key: 'g',
                  render: (_, r) => r.guardian_name
                    ? <>
                        {r.guardian_name}
                        {r.guardian_phone && <div style={{ fontSize: 11, color: '#8a94a6', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.guardian_phone}</div>}
                      </>
                    : <Typography.Text type="secondary">no primary guardian</Typography.Text> },
                { title: 'Choice', dataIndex: 'choice', key: 'c', width: 120,
                  render: (v: 'yes' | 'no' | null) =>
                    v === 'yes' ? <Tag color="success">yes</Tag>
                    : v === 'no' ? <Tag color="error">no</Tag>
                    : <Tag>pending</Tag> },
                { title: 'When', dataIndex: 'at', key: 'w', width: 150,
                  render: (v: string | null) => v ? new Date(v).toLocaleString() : <Typography.Text type="secondary">—</Typography.Text> },
                { title: '', key: 'act', width: 170,
                  render: (_, r) => (
                    <Space size={4}>
                      <Button size="small" onClick={() => submit.mutate({ student_id: r.student_id, choice: 'yes' })}
                        disabled={!r.guardian_id || r.choice === 'yes'}>Yes</Button>
                      <Button size="small" danger onClick={() => submit.mutate({ student_id: r.student_id, choice: 'no' })}
                        disabled={!r.guardian_id || r.choice === 'no'}>No</Button>
                    </Space>
                  ) },
              ]}
            />
          )}
      </Card>
    </Space>
  );
}

/* ================== Cert verifier ================== */

export function CertVerifierPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [params, setParams] = useSearchParams();
  const seed = params.get('number') ?? '';
  const [num, setNum] = useState(seed);
  const [result, setResult] = useState<IssuedCertVerification | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function verify() {
    if (!num.trim()) return;
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await productApi.verifyCert(institution, num.trim());
      setResult(r);
      const next = new URLSearchParams(params);
      next.set('number', num.trim());
      setParams(next, { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setErr('No certificate found with this number.');
      } else {
        setErr(extractErrMsg(e) ?? 'Verify failed');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Verify certificate</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Look up a certificate number (TC / bonafide / character / custom) to confirm it was issued here.
        </Typography.Text>
      </div>
      <Card size="small">
        <Space size={8}>
          <Input value={num} onChange={(e) => setNum(e.target.value.toUpperCase())}
            onPressEnter={verify}
            placeholder="e.g. TC-2025-00042 / BF-2026-00013"
            style={{ width: 320, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
          <Button type="primary" icon={<SearchOutlined />} loading={busy} onClick={verify}
            disabled={!num.trim()}>Verify</Button>
        </Space>
      </Card>

      {err && <Alert type="warning" showIcon message={err} />}
      {result && (
        <Card size="small">
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <SafetyCertificateOutlined style={{ fontSize: 28, color: result.valid ? '#1c6442' : '#a24040' }} />
              <div>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  {result.kind === 'transfer_certificate' ? 'Transfer certificate'
                    : result.kind === 'bonafide' ? 'Bonafide certificate'
                    : result.kind === 'character' ? 'Character certificate'
                    : 'Certificate'}
                </Typography.Title>
                <Typography.Text type="secondary" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{result.number}</Typography.Text>
              </div>
              <div style={{ flex: 1 }} />
              <Tag color={result.valid ? 'success' : 'error'}>{result.valid ? 'valid' : 'invalid / expired'}</Tag>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginTop: 8, fontSize: 13 }}>
              <KV k="Issued on" v={result.issued_on} />
              <KV k="Student" v={result.student_name} />
              <KV k="Admission" v={result.admission_number} />
              <KV k="Institution" v={result.institution_name} />
              <KV k="Purpose / reason" v={result.purpose_or_reason ?? '—'} />
            </div>
            {result.note && <Alert type="info" showIcon message={result.note} />}
          </Space>
        </Card>
      )}
    </Space>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{k}</div>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{v}</div>
    </div>
  );
}
